/** The activity category record's binding of the records core (plan §4, §6, §9.7, §10.3; O8):
 *  what the page's `DraftProvider` and `RecordEditorProvider` need, in view mode and in edit
 *  mode.
 *
 *  - **Baselines** — the editable fields' stored values in the changeset's wire form. They
 *    come from edit_context (`baselines`). Until it arrives, the title and the access tags'
 *    ids read from the detail (`categoryBaselines`) so the card renders at once — but not the
 *    icon: its wire form is its Image id, which only edit_context carries (the detail has its
 *    CDN keys). The card shows the detail's icon meanwhile, and nothing can change it before
 *    edit_context is here.
 *  - **The icon** — the stored image the field shows is edit_context's Image id drawn from the
 *    detail's CDN keys (`categoryStoredIcon`); a picked file uploads at once and the draft
 *    holds the new id (`widgets.tsx`, D3).
 *  - **Fields** — the server's edit catalog (`activityCategoryCatalogField`), else the
 *    registry, which agrees with it (the contract test). The icon's help is the console's own
 *    words: the catalog's speaks to API clients (the staging endpoint).
 *  - **Editability** — the `ConsoleCatalogEditEnabled` kill switch ("Editing is switched
 *    off"), `console_activities_manage`, an owning operator, each field's permissions and
 *    guards.
 *  - **Preview / apply** — the category changeset's (`useActivityCategoryChangesetPreview`,
 *    `useApplyActivityCategoryChangeset`). No feed imports a category: no verdicts, no
 *    "managed by". It takes no questions and fires no events.
 *  - **Names** — the access tags the detail names, and the stored icon by its type (the
 *    ledger's "Icon: PNG icon → kayak.svg" — its CDN key is no file name, `categoryIconName`);
 *    the pickers and the upload (by the picked file's name) add what the operator picks. */
import { useMemo } from 'react';

import type { ActivityCategoryDetail } from '@/api/activityCategories';
import {
  useActivityCategoryChangesetPreview,
  useApplyActivityCategoryChangeset,
  type ActivityCategoryRecordCatalog,
  type ActivityCategoryRecordEditableFieldKey,
  type ActivityCategoryRecordEditContext,
} from '@/api/activityCategoryRecord';
import { CATALOG_EDITING_OFF, CATALOG_NO_OPERATOR } from '@/features/catalog/editGate';
import { recordKey, type RecordChangeset, type RecordKey } from '@/features/records/draft/changeSet';
import type {
  RecordEditability,
  RecordEditorConfig,
  RecordField,
  RecordOptionRef,
  RecordPreviewQuery,
} from '@/features/records/editor/types';
import type { StoredImage } from '@/features/records/fields/widgets/ImageWidget';

import { categoryTitle } from './parts/CategoryRecordHeader';
import { ACTIVITY_CATEGORY_ENTITY, activityCategoryCatalogField, activityCategoryRecordFields } from './registry';
import { useCategoryWidgets } from './widgets';

export const CATEGORY_MANAGE_PERM = 'console_activities_manage';

/** The record's own times (a receipt, "changed on the server at …", a restored draft) read in
 *  UTC, labelled — as the activity's do. A category has no wall-clock field. */
export const CATEGORY_TIMEZONE = 'UTC';

/** The record key of the category (`activity_category:<id>`). */
export const categoryRec = (id: number): RecordKey => recordKey(ACTIVITY_CATEGORY_ENTITY, id);

/** The editable fields' stored values the detail can give, in their wire form: the title and
 *  the access tags' ids, ascending. Not the icon — see above. */
export function categoryBaselineValues(
  c: ActivityCategoryDetail,
): Partial<Record<ActivityCategoryRecordEditableFieldKey, unknown>> {
  return {
    title: c.title,
    atag: c.atag.map((t) => t.id).sort((x, y) => x - y),
  };
}

/** The draft's baselines from the detail (`"activity_category:<id>"` → field → value). */
export function categoryBaselines(c: ActivityCategoryDetail): Record<RecordKey, Record<string, unknown>> {
  return { [categoryRec(c.id)]: categoryBaselineValues(c) };
}

/** The stored icon's Image id, from edit_context's baselines; null when it has none, or
 *  before edit_context is here. */
export function categoryIconId(
  c: Pick<ActivityCategoryDetail, 'id'>,
  ctx: Pick<ActivityCategoryRecordEditContext, 'baselines'> | null | undefined,
): number | null {
  const id = ctx?.baselines[categoryRec(c.id)]?.icon;
  return typeof id === 'number' ? id : null;
}

/** The stored icon in words. Its CDN key is no file name — `image-<name>-<56-hex hash>.png`
 *  on dev — and neither the detail nor edit_context carries the Image's own name, so the
 *  icon is named by its type: "PNG icon", "SVG icon"; "Icon" when the key has no extension. */
export function categoryIconName(icon: { s: string }): string {
  const ext = /\.([a-z0-9]{2,5})$/i.exec(icon.s)?.[1];
  return ext ? `${ext.toUpperCase()} icon` : 'Icon';
}

/** The detail's icon as the image row draws it (view mode): its CDN keys, and its type as the
 *  name (`categoryIconName`). The row shows no id while it has the keys, and the detail
 *  carries none. */
export function categoryIconImage(c: Pick<ActivityCategoryDetail, 'icon'>): StoredImage | null {
  return c.icon ? { id: 0, url: null, p: c.icon.p, s: c.icon.s, name: categoryIconName(c.icon) } : null;
}

/** The stored icon as the image input knows it: edit_context's Image id, drawn from the
 *  detail's CDN keys; null without one. */
export function categoryStoredIcon(c: Pick<ActivityCategoryDetail, 'icon'>, iconId: number | null): StoredImage | null {
  if (iconId === null) return null;
  return { ...(categoryIconImage(c) ?? { url: null, p: null, s: null, name: null }), id: iconId };
}

/** The picked records the detail names, by field: the access tags, and the stored icon by
 *  its type (`categoryIconName`). */
export function categoryOptionRefs(c: ActivityCategoryDetail, iconId: number | null = null): Record<string, RecordOptionRef[]> {
  return {
    atag: c.atag.map((t) => ({ id: t.id, label: t.name ?? `Access tag #${t.id}`, group: 'Access tags' })),
    icon: iconId !== null && c.icon ? [{ id: iconId, label: categoryIconName(c.icon) }] : [],
  };
}

/** Where a read-only value comes from — its tooltip. */
const PROVENANCE: Readonly<Record<string, string>> = {
  activity_count: 'The activities in this category — a delete un-categorises them, it does not delete them',
};

/** Help in the operator's words where the catalog's speaks to API clients. */
const FIELD_HELP: Readonly<Partial<Record<string, string>>> = {
  icon:
    'Shown beside the category in the app. JPG, PNG, GIF, WebP or SVG, at most 10 MB. A picked file uploads at ' +
    'once and becomes the icon when you save; the replaced icon is deleted unless something else shows it.',
};

const withHelp = (f: RecordField): RecordField => {
  const help = FIELD_HELP[f.key];
  return help ? { ...f, help } : f;
};

/** Why the editor cannot open on this category, or null — the permission aside (no
 *  permission, no Edit at all). The switch's words win: they say the environment, not the
 *  record. */
export function categoryEditBlockedReason({
  catalog,
  editContext,
}: {
  catalog?: Pick<ActivityCategoryRecordCatalog, 'enabled'> | null;
  editContext?: Pick<ActivityCategoryRecordEditContext, 'flags' | 'tenant'> | null;
}): string | null {
  if (catalog?.enabled === false || editContext?.flags.catalog_edit_enabled === false) return CATALOG_EDITING_OFF;
  if (editContext && !editContext.tenant) return CATALOG_NO_OPERATOR;
  return null;
}

/** The category's changeset preview as the editor reads it. */
export function useCategoryPreview(id: number, body: RecordChangeset | null): RecordPreviewQuery {
  const q = useActivityCategoryChangesetPreview(id, body);
  return {
    data: q.data,
    isFetching: q.isFetching,
    isPlaceholderData: q.isPlaceholderData,
    error: q.error,
    // Asked nothing (no change of the category in the body): settled, not loading.
    isEnabled: q.isEnabled,
  };
}

export interface CategoryEditorInput {
  detail: ActivityCategoryDetail;
  /** The server's edit catalog and the category's edit context; until both are here, view
   *  mode from the registry and the detail. */
  catalog?: ActivityCategoryRecordCatalog | null;
  editContext?: ActivityCategoryRecordEditContext | null;
  /** A ledger row for a field that is not on screen (the History tab). */
  onFocusMissing?: (rec: RecordKey, path: string) => void;
}

const REGISTRY_FIELDS = activityCategoryRecordFields().map(withHelp);
const NONE: never[] = [];

/** What both modes share: the detail's facts. */
function sharedParts(
  detail: ActivityCategoryDetail,
  iconId: number | null,
): Omit<RecordEditorConfig, 'fields' | 'usePreview' | 'useApply' | 'widgets'> {
  return {
    options: NONE,
    actions: NONE,
    effects: NONE,
    timezone: CATEGORY_TIMEZONE,
    recordNoun: 'category',
    // No feed imports a category; were one to, the catalog's word.
    managedBy: null,
    feedEvent: 'sync',
    verdictOf: () => null,
    provenanceOf: (field) => PROVENANCE[field.key],
    optionRefs: categoryOptionRefs(detail, iconId),
  };
}

/** The editing half (catalog + edit context), pure — for tests. */
export function categoryEditorConfigParts({
  detail,
  catalog,
  editContext: ctx,
  onFocusMissing,
}: CategoryEditorInput & {
  catalog: ActivityCategoryRecordCatalog;
  editContext: ActivityCategoryRecordEditContext;
}): Omit<RecordEditorConfig, 'usePreview' | 'useApply' | 'widgets'> {
  const rec = categoryRec(detail.id);
  const blocked = categoryEditBlockedReason({ catalog, editContext: ctx });

  const editabilityOf = (r: RecordKey, field: RecordField): RecordEditability => {
    if (blocked) return { editable: false, reason: blocked };
    if (!ctx.permissions[CATEGORY_MANAGE_PERM]) {
      return { editable: false, reason: `Needs the ${CATEGORY_MANAGE_PERM} permission` };
    }
    const missing = field.permissions.find((p) => !ctx.permissions[p]);
    if (missing) return { editable: false, reason: `Needs the ${missing} permission` };
    const guards = ctx.blocked[`${r}:${field.key}`] ?? [];
    if (guards.length > 0) return { editable: false, reason: guards.map((g) => catalog.guards[g] ?? g).join(' ') };
    return { editable: true };
  };

  return {
    ...sharedParts(detail, categoryIconId(detail, ctx)),
    fields: catalog.fields.map(activityCategoryCatalogField).map(withHelp),
    effects: catalog.effects,
    editabilityOf,
    groupOf: () => ({ id: ACTIVITY_CATEGORY_ENTITY, title: categoryTitle(detail) }),
    recordOrder: [rec],
    maxChanges: catalog.max_changes,
    descriptionMaxLength: catalog.description_max_length,
    ...(onFocusMissing ? { onFocusMissing } : {}),
  };
}

/** The category's `RecordEditorConfig` — stable while its inputs are. */
export function useCategoryEditorConfig({
  detail,
  catalog,
  editContext,
  onFocusMissing,
}: CategoryEditorInput): RecordEditorConfig {
  const iconId = categoryIconId(detail, editContext);
  const widgets = useCategoryWidgets(detail, categoryStoredIcon(detail, iconId));
  const id = detail.id;
  return useMemo<RecordEditorConfig>(() => {
    // The same hooks in both halves (React's hook order): until the editor's data is here the
    // page is in view mode, where the editor asks no preview and nothing saves.
    const hooks: Pick<RecordEditorConfig, 'usePreview' | 'useApply'> = {
      usePreview: (body) => useCategoryPreview(id, body),
      useApply: (opts) => useApplyActivityCategoryChangeset(id, opts),
    };
    if (!catalog || !editContext) {
      return { ...sharedParts(detail, null), fields: REGISTRY_FIELDS, description: false, widgets, ...hooks };
    }
    return { ...categoryEditorConfigParts({ detail, catalog, editContext, onFocusMissing }), widgets, ...hooks };
  }, [detail, catalog, editContext, onFocusMissing, widgets, id]);
}
